import Cookies from "js-cookie";
import { add_to_cart } from "../gtm";
import { routes } from "../utils/routes";
import { usePathname } from "next/navigation";
import { constants } from "../utils/constants";
import { addToTrolley } from "../services/cart";
import { useAppDispatch, useAppSelector } from "../store";
import { htmlEntityToText } from "../utils/browserMethods";
import { fireOpenCartViewEvent } from "../utils/livesearch";
import {
  endCommonLoading,
  startCommonLoading,
} from "../store/reducers/uiState";
import {
  addToCartCtx,
  getProductsMap,
  handleToastError,
  getProductsSKUMap,
  handleToastProduct,
  handleThunkResponse,
  handleGraphqlResponse,
  returnShoppingCartCtx,
  mutateProductInfo,
  formatRemovedProductGA4,
} from "../utils/functions";
import {
  getCartProducts,
  updateUniqCount,
  updateTotalCount,
  addOrUpdateProducts,
  addOrUpdateCartPrice,
  addOrUpdateFreeGifts,
  fetchCartForGuestUser,
  fetchCartForLoggedInUser,
  updateIsHomedeliveryAvailable,
  getAppliedCoupons,
  addOrUpdateAppliedCoupons,
} from "../store/reducers/cart";

export const useAddToTrolley = () => {
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const cartProducts = useAppSelector(getCartProducts);
  const appliedCoupons = useAppSelector(getAppliedCoupons);

  const handleAddToTrolley = async ({ product, onSuccess = () => {} }) => {
    const { soldInPairs = 0 } = product;
    const showCTA = pathname !== routes.basket;

    dispatch(startCommonLoading());
    const tokenCipher = Cookies.get(constants.cookies.customerToken);
    const cartId = localStorage.getItem(constants.localStorage.cartId);

    try {
      if (!cartId) {
        if (!tokenCipher) {
          await dispatch(fetchCartForGuestUser()).then(handleThunkResponse);
        } else {
          await dispatch(fetchCartForLoggedInUser()).then(handleThunkResponse);
        }
      }

      localStorage.setItem(constants.localStorage.shouldFetchCartDetails, 1);
      const apiRes = await addToTrolley({
        cartItems: [{ sku: product?.sku, quantity: soldInPairs == 1 ? 2 : 1 }],
      });

      const data = handleGraphqlResponse(
        apiRes,
        (d) => d?.addProductsToCart?.user_errors,
      );

      const userCart = data?.addProductsToCart?.cart || {};
      const appliedCoupons = data?.addProductsToCart?.cart?.applied_coupons;
      const mappedItem = getProductsMap(userCart?.itemsV2?.items);

      const freeGiftMap = mappedItem?.freeGifts;
      const productsMap = mappedItem?.cartProducts;

      const cartPrice = userCart?.prices || {};
      const totalQuantity = userCart?.total_quantity;
      dispatch(addOrUpdateProducts(productsMap));
      dispatch(addOrUpdateFreeGifts(freeGiftMap));
      dispatch(addOrUpdateCartPrice(cartPrice));
      dispatch(updateTotalCount(totalQuantity));
      dispatch(addOrUpdateAppliedCoupons(appliedCoupons));
      dispatch(updateUniqCount(userCart?.itemsV2?.total_count));
      dispatch(
        updateIsHomedeliveryAvailable(userCart?.is_home_delivery_available),
      );

      handleToastProduct({
        name: htmlEntityToText(product?.name),
        image: product?.thumbnail?.url,
        showCTA,
      });

      const matchedItem = Object.values(productsMap).find(
        (e) =>
          !e?.isFreegift &&
          e.product?.sku?.toLowerCase() === product?.sku?.toLowerCase(),
      );
      const mutated = mutateProductInfo(matchedItem, true);
      add_to_cart({
        currency: mutated?.prices?.price_including_tax?.currency,
        value: mutated?.prices?.price_including_tax?.value?.toFixed(2),
        items: formatRemovedProductGA4(
          [{ ...matchedItem.product, prices: mutated?.prices, quantity: 1 }],
          appliedCoupons,
          true,
        ),
      });

      onSuccess?.();
      if (!Object.keys(cartProducts)?.length) {
        fireOpenCartViewEvent(
          returnShoppingCartCtx(
            +getProductsSKUMap(Object.values(productsMap)),
            cartPrice,
            userCart?.itemsV2?.total_count,
          ),
        );
      }
      addToCartCtx(
        product?.sku,
        1,
        getProductsSKUMap(Object.values(productsMap)),
        getProductsSKUMap(Object.values(cartProducts)),
        cartPrice,
        userCart?.itemsV2?.total_count,
      );
    } catch (error) {
      console.error("error", error);
      handleToastError(error.message);
    }
    dispatch(endCommonLoading());
  };

  return { handleAddToTrolley };
};
